import { useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { noop } from 'lodash';

import { IDocumentsGetResponse } from '@/shared/endpoints/documents/models/response';
import type { SxProps } from '@mui/system';
import { Theme, useTheme } from '@mui/material/styles';
import { Alert, Box, Button } from '@/shared/ui';
import DocumentImage from '../document-image';

import UploadImage from '@/assets/icons/upload-image.svg';
import UploadImageSimple from '@/assets/icons/upload.svg';

import { checkFileAccept } from '@/shared/helpers/input';
import { toast } from 'react-toastify';
import { processImageWithConvert } from '@/shared/helpers/input';
import { checkIsImageBroken } from '@/shared/helpers/image';

interface IProps {
    invalid?: boolean;
    document?: IDocumentsGetResponse; // Hopefully this will cover our needs for upload
    file?: File;
    onUpload?(file?: File): Promise<void>;
    boxSx?: SxProps<Theme>;
    className?: string;
    style?: 'simple' | 'base';
    placeholder?: string;
    disabled?: boolean;
    testId?: string;
}

const inputAccept = '.jpg, .jpeg, .png, .heic';

export const DocumentUpload = ({
    testId,
    boxSx,
    invalid,
    file,
    onUpload,
    document,
    style = 'base',
    placeholder,
    disabled: _disabled
}: IProps) => {
    const theme = useTheme();
    const localFilePath = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
    const [loading, setLoading] = useState(false);
    const disabled = _disabled || loading;
    const [errorMessage, setErrorMessage] = useState<{ heading: string; message: string } | null>(null);

    useEffect(() => {
        return () => {
            localFilePath && URL.revokeObjectURL(localFilePath);
        };
    }, [localFilePath]);

    const invalidSx = invalid ? { border: '.125rem solid', borderColor: 'error.main' } : {};

    type ErrorCode =
        | 'HEIC_PROCESSING_FAILED'
        | 'BROKEN_IMAGE'
        | 'INVALID_FORMAT';
    type Result =
        | { accepted: true; file: File }
        | { accepted: false; error: ErrorCode };

    const handleCheckFile = async (file: File): Promise<Result> => {
        const { accepted, format } = checkFileAccept(inputAccept, file);
        if (accepted) {
            let fileToProcessAndCheck = file;
            if (format === 'image/heic') {
                try {
                    fileToProcessAndCheck = await processImageWithConvert(file);
                } catch (error) {
                    return { accepted: false, error: 'HEIC_PROCESSING_FAILED' };
                }
            }
            const isBroken = await checkIsImageBroken(fileToProcessAndCheck);
            if (isBroken) {
                return { accepted: false, error: 'BROKEN_IMAGE' };
            }
            return {
                accepted: true,
                file: fileToProcessAndCheck
            }
        }

        return {
            accepted: false,
            error: 'INVALID_FORMAT'
        }
    };

    const handleUpload = async (files: File[]) => {
        if (files?.length) {
            setLoading(true);
            const res = await handleCheckFile(files[0]);
            if (!res.accepted) {
                const { error } = res;
                const genericImageError =
                    "We couldn't process this image. Please try another file.";

                const errorMessages: Record<ErrorCode, { heading: string; message: string }> = {
                    HEIC_PROCESSING_FAILED: { heading: 'Image Processing Failed', message: genericImageError },
                    BROKEN_IMAGE: { heading: 'Image Processing Failed', message: genericImageError },
                    INVALID_FORMAT: {
                        heading: 'Invalid File Type',
                        message: `Accepted formats ${inputAccept.split(',').map(format => format.toUpperCase()).join(' ')}`,
                    }
                }
                const message = errorMessages[error];
                setErrorMessage(message);
            } else {
                setErrorMessage(null);
                await onUpload?.(res.file);
            }
            setLoading(false);
        }
    }

    if (localFilePath || document) {
        return (
            <div className="flex flex-col gap-y-[.9375rem]">
                <Dropzone onDrop={handleUpload} disabled={disabled}>
                    {({ getInputProps, getRootProps }) => {
                        const rootProps = getRootProps();
                        return (
                            <Box
                                {...rootProps}
                                onClick={noop}
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    height: 160,
                                    [theme.breakpoints.up('lg')]: { padding: 0 },
                                    [theme.breakpoints.down('lg')]: { padding: 0 },
                                    border: errorMessage ? `.125rem solid #FF0707` : 'none',
                                    ...invalidSx,
                                    ...boxSx
                                }}
                                fullWidth
                                shadow
                                data-testid={testId}
                            >
                                {document ? (
                                    <DocumentImage bg path={document.path} />
                                ) : (
                                    <div
                                        className="h-full w-full"
                                        style={{ background: `url('${localFilePath}') center/contain no-repeat` }}
                                    />
                                )}

                                <Button
                                    theme="secondary"
                                    variant="contained"
                                    sx={{ position: 'absolute', top: '10px', background: 'rgba(0, 61, 51, 0.75)', right: '10px' }}
                                    onClick={rootProps.onClick}
                                    disabled={disabled}
                                    loading={disabled}
                                    testId="button-document-upload-change"
                                >
                                    Change
                                    <input data-testid="document-upload-change-input" {...getInputProps()} />
                                </Button>
                            </Box>
                        );
                    }}
                </Dropzone>
                {errorMessage && <Alert label={errorMessage} />}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-y-[.9375rem]">
            <Dropzone onDrop={handleUpload} disabled={loading}>
                {({ getInputProps, getRootProps }) => {
                    return (
                        <Box
                            {...getRootProps()}
                            sx={{
                                ...invalidSx,
                                ...boxSx,
                                bgcolor: style === 'base' ? 'primary.100' : 'primary.50',
                                borderRadius: style === 'base' ? '12px' : '4px',
                                border: errorMessage ? `2px solid #FF0707` : 'none'
                            }}
                            fullWidth
                            shadow
                        >
                            <div
                                className={`flex flex-col items-center justify-center ${!localFilePath && !document ? 'cursor-pointer' : ''}`}
                            >
                                {!localFilePath && !document && (
                                    <>
                                        <img
                                            src={style === 'base' ? UploadImage : UploadImageSimple}
                                            alt="upload image"
                                            className="mb-[1.1875rem]"
                                        />
                                        <p className="text-center font-atkinsonHyperlegible font-normal">
                                            {placeholder || 'Click here to browse your images or you may drop files here.'}
                                        </p>
                                    </>
                                )}

                                <input data-testid="document-upload-input" {...getInputProps()} />
                            </div>
                        </Box>
                    );
                }}
            </Dropzone>
            {errorMessage && <Alert removeBorder label={
                <div className='flex flex-col gap-[.25rem]'>
                    <span className='block font-atkinsonBold font-bold text-18 leading-[137%]'>
                        {errorMessage.heading}
                    </span>
                    <span className='block font-dmSans font-normal text-16 leading-[125%]'>
                        {errorMessage.message}
                    </span>
                </div>
            } />}
        </div>
    );
};
